import { useState } from 'react'
import { Plus } from 'lucide-react'
import { ErpTable, Panel, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtDate } from '@/shared/lib/format'
import type { SurveyPopulationFrameDto, SurveyQuotaDimensionDto } from '@/shared/api/types'
import { setPopulationFrameRow } from './api'
import { count, pct } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * The population this poll will be weighted to, and where each number came from.
 *
 * ## Nothing else in the product can enter one
 *
 * Which is why every estimate is suppressed today. Suppression needs a weight
 * run; a weight run that has no frame can only produce design weights, stamps
 * its output `NOT_WEIGHTED`, and the Results screen renders a sentence instead
 * of a number. This table is the fix, and it is a table of about eight rows.
 *
 * ## `sourceNote` is required and the form says why
 *
 * "Weighted to the population" is a claim, and a claim has to name the
 * population — KNBS 2019, an IEBC register extract, the previous wave. The
 * server refuses a blank one with that sentence. A frame row with no provenance
 * is a number somebody made up, and every estimate raked to it inherits that
 * without anybody downstream being able to tell.
 *
 * ## The share column is arithmetic on the user's own input
 *
 * Rule 2 forbids the client computing a percentage, and it exists so a
 * client-computed share cannot disagree with a server-computed one sitting next
 * to it. There is no server-computed share of the frame — `PopulationFrameResponse`
 * carries a count and nothing else — so there is nothing to disagree with, and
 * the input is not survey data: it is the numbers the person typed into this
 * form, divided by their own total.
 *
 * It earns its place because the counts alone are unreadable. 1,242,000 against
 * 1,170,528 is not obviously 51/49, and 51/49 is the shape somebody meant to
 * enter; a transposed digit shows up instantly as 84/16 and not at all as a
 * seven-figure number. The column is labelled "share of the frame you entered"
 * rather than "share" so it cannot be mistaken for a survey estimate.
 */
interface Draft {
  dimensionCode: string
  valueCode: string
  populationCount: string
  sourceNote: string
  asOf: string
  /** Set when editing an existing row — the key halves are then locked. */
  existingId: string | null
  /**
   * Which group the form is drawn in, fixed when it opens.
   *
   * Deliberately NOT `dimensionCode`. Keying the position off the live text
   * moves the form to a different place in the tree the moment the typed code
   * matches an existing group — React unmounts and remounts it, and the caret
   * lands nowhere on the last keystroke of the word.
   */
  anchor: string | null
}

const emptyDraft = (dimensionCode: string): Draft => ({
  dimensionCode, valueCode: '', populationCount: '', sourceNote: '', asOf: '',
  existingId: null, anchor: dimensionCode === '' ? null : dimensionCode,
})

export function PopulationFrameEditor({ surveyId, rows, dimensions, canUpdate, onSaved }: {
  surveyId: string
  rows: SurveyPopulationFrameDto[]
  /** Quota dimensions — for the raking mismatch warning and for the value-code lists. */
  dimensions: SurveyQuotaDimensionDto[]
  canUpdate: boolean
  onSaved: () => void
}) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Every dimension that should have frame rows, whether or not it has any.
   *
   * A quota dimension with no rows must still appear, as an empty group with the
   * warning attached. Listing only what exists would hide the exact gap this
   * panel is here to surface.
   */
  const groups = [...new Set([
    ...dimensions.map((d) => d.code),
    ...rows.map((r) => r.dimensionCode),
  ])].sort()

  const frameCodes = new Set(rows.map((r) => r.dimensionCode))
  const unmatched = dimensions.filter((d) => !frameCodes.has(d.code))

  async function save() {
    if (!draft) return
    setBusy(true)
    setError(null)
    try {
      await setPopulationFrameRow(surveyId, {
        dimensionCode: draft.dimensionCode.trim(),
        valueCode: draft.valueCode.trim(),
        // Not `Number(x) || 0`. An empty box is "you have not said" and a zero
        // is "nobody lives here"; NaN serialises to JSON `null`, which is the
        // wire value for the first, and the server answers it with the sentence
        // that says a population count has no default.
        populationCount: draft.populationCount.trim() === '' ? Number.NaN
          : Number(draft.populationCount),
        sourceNote: draft.sourceNote,
        asOf: draft.asOf.trim() === '' ? null : draft.asOf,
      })
      setDraft(null)
      onSaved()
    } catch (e) {
      // `survey/frame-source-required` among others, rendered in the server's words.
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel
      title="Population frame"
      description="What the poll is weighted TO. One row per value of every dimension it will be raked on."
      right={canUpdate && (
        <button type="button" className="erp-btn h-8 text-erp-xs"
          onClick={() => setDraft(emptyDraft(''))}>
          <Plus className="h-3 w-3" /> Add frame row
        </button>
      )}
    >
      <div className="space-y-4">
        <ErrorStrip message={error} />

        {unmatched.length > 0 && (
          // Loud on purpose. The readiness panel says this too, in the server's
          // words — but a warning at the top of the page and the table you would
          // fix it in are different places, and this is the second one.
          <Note tone="bad">
            {unmatched.length === 1
              ? `The quota dimension ‘${unmatched[0]?.name || unmatched[0]?.code}’ has no frame rows.`
              : `${unmatched.length} quota dimensions have no frame rows: `
                + unmatched.map((d) => d.name || d.code).join(', ') + '.'}
            {' '}Raking needs a target for every margin it rakes on, so this is a weight run that
            fails at three in the morning rather than a form that objects now. Add a row for each
            of its values below, or drop the dimension.
          </Note>
        )}

        {groups.length === 0 ? (
          <Note tone="bad">
            No population frame at all. Until there is one, a weight run can only produce design
            weights of 1, every estimate it makes is stamped NOT_WEIGHTED, and the Results screen
            has a sentence where the number should be. Nothing else in this product can enter
            these rows.
          </Note>
        ) : groups.map((dimensionCode) => (
          <FrameGroup
            key={dimensionCode}
            dimensionCode={dimensionCode}
            rows={rows.filter((r) => r.dimensionCode === dimensionCode)}
            dimension={dimensions.find((d) => d.code === dimensionCode) ?? null}
            canUpdate={canUpdate}
            draft={draft?.anchor === dimensionCode ? draft : null}
            busy={busy}
            onDraft={setDraft}
            onSave={save}
          />
        ))}

        {/* An unanchored draft — "Add frame row" rather than "Add value" — which
            is how a margin with no quota dimension on it gets its first row. */}
        {draft && draft.anchor === null && (
          <div className="erp-card p-3">
            <DraftForm draft={draft} dimension={null} busy={busy}
              onDraft={setDraft} onSave={save} />
          </div>
        )}

        <p className="text-erp-xs text-muted-foreground">
          A frame row is a claim about the population, not about the sample. The share column is
          arithmetic on the counts entered here — it is shown so the shape of the frame is
          checkable at a glance, and it is not a survey estimate.
        </p>
      </div>
    </Panel>
  )
}

function FrameGroup({
  dimensionCode, rows, dimension, canUpdate, draft, busy, onDraft, onSave,
}: {
  dimensionCode: string
  rows: SurveyPopulationFrameDto[]
  dimension: SurveyQuotaDimensionDto | null
  canUpdate: boolean
  draft: Draft | null
  busy: boolean
  onDraft: (d: Draft | null) => void
  onSave: () => void
}) {
  const total = rows.reduce((n, r) => n + r.populationCount, 0)

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 pb-1.5">
        <div className="min-w-0">
          <span className="text-erp-sm font-medium font-mono">{dimensionCode}</span>
          {dimension && (
            <span className="ml-2 text-erp-xs text-muted-foreground">
              {dimension.name} · {dimension.cells.length} quota cell
              {dimension.cells.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {canUpdate && !draft && (
          <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs text-chocolate"
            onClick={() => onDraft(emptyDraft(dimensionCode))}>
            Add value
          </button>
        )}
      </div>

      <ErpTable head={(
        <>
          <Th>Value</Th>
          <Th className="text-right">Population</Th>
          <Th className="text-right"
            title="This value's count divided by the total of the counts entered for this dimension">
            Share of the frame you entered
          </Th>
          <Th>Source</Th>
          <Th>As of</Th>
          <Th />
        </>
      )}>
        {rows.length === 0 && !draft ? (
          <tr>
            <td colSpan={6} className="px-4 py-6 text-center text-erp-sm text-muted-foreground">
              No rows for {dimensionCode}. Nothing can be raked to this dimension.
            </td>
          </tr>
        ) : rows.map((r) => (
          <tr key={r.id} className="hover:bg-muted/40">
            <Td className="font-mono text-erp-xs">{r.valueCode}</Td>
            <Td className="text-right tabular-nums">{count(r.populationCount)}</Td>
            <Td className="text-right tabular-nums text-muted-foreground">
              {total > 0 ? pct(r.populationCount / total) : '—'}
            </Td>
            <Td className="text-erp-xs text-muted-foreground" title={r.sourceNote}>
              <span className="line-clamp-2">{r.sourceNote}</span>
            </Td>
            <Td className="text-erp-xs text-muted-foreground whitespace-nowrap">
              {r.asOf ? fmtDate(r.asOf) : '—'}
            </Td>
            <Td className="text-right">
              {canUpdate && (
                <button type="button" className="erp-btn erp-btn-ghost h-7 text-erp-xs text-chocolate"
                  onClick={() => onDraft({
                    dimensionCode: r.dimensionCode, valueCode: r.valueCode,
                    populationCount: String(r.populationCount), sourceNote: r.sourceNote,
                    asOf: r.asOf ?? '', existingId: r.id, anchor: r.dimensionCode,
                  })}>
                  Edit
                </button>
              )}
            </Td>
          </tr>
        ))}

        {rows.length > 1 && (
          <tr className="bg-muted/30">
            <Td className="text-erp-xs uppercase tracking-wide text-muted-foreground">Total</Td>
            <Td className="text-right tabular-nums font-medium">{count(total)}</Td>
            <Td className="text-right tabular-nums text-muted-foreground">
              {total > 0 ? pct(1) : '—'}
            </Td>
            {/* `Td` takes no colSpan — a raw cell rather than three empty ones. */}
            <td colSpan={3} className="erp-grid-cell" />
          </tr>
        )}

        {draft && (
          <tr>
            <td colSpan={6} className="px-3 py-3 border-b border-border bg-muted/20">
              <DraftForm draft={draft} dimension={dimension} busy={busy}
                onDraft={onDraft} onSave={onSave} />
            </td>
          </tr>
        )}
      </ErpTable>
    </div>
  )
}

/**
 * The add/edit row.
 *
 * A `<td colSpan>` rather than one input per column: `sourceNote` is up to 1000
 * characters and the whole point of the field is that somebody writes a real
 * sentence in it. A 90px cell would train people to type "census".
 */
function DraftForm({ draft, dimension, busy, onDraft, onSave }: {
  draft: Draft
  dimension: SurveyQuotaDimensionDto | null
  busy: boolean
  onDraft: (d: Draft | null) => void
  onSave: () => void
}) {
  const set = (patch: Partial<Draft>) => onDraft({ ...draft, ...patch })
  // The cell values the server already knows about for this dimension. Typing a
  // value code that no quota cell uses is allowed — a frame can carry a margin
  // with no quota on it — but the list is offered first.
  const valueCodes = (dimension?.cells ?? []).map((c) => c.valueCode)

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2">
        <label className="col-span-12 sm:col-span-3 space-y-1">
          <span className="text-erp-xs font-medium text-muted-foreground block">
            Dimension<span className="text-red-600 ml-0.5">*</span>
          </span>
          <input className="erp-input h-8 w-full font-mono text-erp-xs" maxLength={32}
            value={draft.dimensionCode} disabled={!!draft.existingId} placeholder="gender"
            onChange={(e) => set({ dimensionCode: e.target.value })} />
        </label>
        <label className="col-span-12 sm:col-span-3 space-y-1">
          <span className="text-erp-xs font-medium text-muted-foreground block">
            Value<span className="text-red-600 ml-0.5">*</span>
          </span>
          {valueCodes.length > 0 && !draft.existingId ? (
            <select className="erp-input h-8 w-full text-erp-sm" value={draft.valueCode}
              onChange={(e) => set({ valueCode: e.target.value })}>
              <option value="">Choose…</option>
              {valueCodes.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ) : (
            <input className="erp-input h-8 w-full font-mono text-erp-xs" maxLength={64}
              value={draft.valueCode} disabled={!!draft.existingId} placeholder="FEMALE"
              onChange={(e) => set({ valueCode: e.target.value })} />
          )}
        </label>
        <label className="col-span-8 sm:col-span-4 space-y-1">
          <span className="text-erp-xs font-medium text-muted-foreground block">
            Population count<span className="text-red-600 ml-0.5">*</span>
          </span>
          <input className="erp-input h-8 w-full text-right tabular-nums" type="number" min={0}
            value={draft.populationCount} placeholder="1242000"
            onChange={(e) => set({ populationCount: e.target.value })} />
        </label>
        <label className="col-span-4 sm:col-span-2 space-y-1">
          <span className="text-erp-xs font-medium text-muted-foreground block">As of</span>
          <DatePicker value={draft.asOf} onChange={(v) => set({ asOf: v })} />
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-erp-xs font-medium text-muted-foreground block">
          Source<span className="text-red-600 ml-0.5">*</span>
        </span>
        <input className="erp-input h-8 w-full text-erp-sm" maxLength={1000}
          value={draft.sourceNote}
          placeholder="KNBS 2019 census, Nairobi County, adults 18+"
          onChange={(e) => set({ sourceNote: e.target.value })} />
      </label>
      <p className="text-erp-xs text-muted-foreground">
        Required, and the reason it is required is the whole panel: "weighted to the population"
        is a claim that has to name the population. KNBS 2019, an IEBC register extract, a
        previous wave — whatever it is, it goes in the export and in the methodology note.
      </p>

      {draft.existingId && (
        // Rule 3 again: two disabled inputs above, and here is the sentence.
        <p className="text-erp-xs text-muted-foreground">
          Dimension and value are locked while editing: the server keys a frame row on the pair,
          so changing either would write a second row rather than move this one. Cancel and add
          a new row if the pair is what is wrong.
        </p>
      )}

      <div className="flex items-center gap-2">
        <button type="button" className="erp-btn erp-btn-primary h-8 text-erp-xs"
          disabled={busy} onClick={onSave}>
          {busy ? 'Saving…' : draft.existingId ? 'Save row' : 'Add row'}
        </button>
        <button type="button" className="erp-btn erp-btn-ghost h-8 text-erp-xs"
          onClick={() => onDraft(null)}>
          Cancel
        </button>
      </div>
    </div>
  )
}
